// The page header's action row, shared by the URL Policies, Access Policies and Browser
// Controls list pages.
//
// All three referenced `.headerButtons` from this sheet and it was never DEFINED. Two of them
// carry a single button so nothing showed; the moment a second one was added they stacked at
// different widths, because a v2 Button carrying an `svg` picks up `.hasSvg { display: flex }`
// — which is BLOCK-level — and the wrapper was a plain block div. Nothing errored: a missing
// CSS-module key is `undefined`, and classnames drops it silently.
//
// Defined here rather than per page so the next second button does not reproduce it. The
// buttons need no width prop: v2's `pi-*` padding utilities set `width: unset`, so each sizes
// to its own label once the container lays them out in a row.
.headerButtons {
  display: flex;
  align-items: center;
  // 10px, not the header title's 22px: these belong together as one action group, and a gap
  // that wide reads as unrelated controls.
  gap: 10px;
  // The header inner is `justify-content: space-between` with the title on `flex: 1`, so
  // without this the button group is what gives when the title is long — and a truncated
  // button label is worse than a truncated title.
  flex-shrink: 0;
}

// For the AuthorizedAction that groups the header's actions under one permission check.
//
// It renders a wrapper div, so without this the wrapper is the only child of .headerButtons —
// the row's flex lays out the wrapper and the buttons inside it stack (each is block-level via
// v2 Button's `.hasSvg { display: flex }`). Passed as AuthorizedAction's `className` rather
// than reached with a `> div` selector, so the layout is visible at the call site.
.headerButtonsGroup {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.dlpModal {
  width: 800px;
  max-width: calc(100vw - 48px);
  padding: 24px 12px;

  .main {
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow: auto;
    padding: 4px 24px 8px;
    margin: 0 -24px;
  }

  .dlpModalBody {
    padding: 0 16px;
    display: flex;
    flex-direction: column;
  }

  .title {
    font-size: 20px;
    font-weight: 500;
    padding: 0 16px 18px;
  }
}

.modalSection {
  overflow: visible;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .stepContent {
    padding: 0 !important;
  }
}

.sectionToolbar {
  display: flex;
  justify-content: flex-end;
  flex: 1;
}

.importBtn {
  margin-left: auto;
}

.sectionHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 !important;
}

.modalTextArea {
  display: flex;
}

.stickyHeader {
  position: sticky;
  top: -4px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;

  @include themify($themes) {
    @include dark-mode() {
      background-color: themed("background");
    }
    @include light-mode() {
      background-color: themed("main-secondary");
    }
  }
}

.conditionsRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  .condition {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 999px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;

    @include themify($themes) {
      background-color: themed("main-secondary");
      color: themed("main-primary");
      border: 1px solid themed("neutral-secondary");
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
    }

    &:hover {
      @include themify($themes) {
        border-color: rgba(themed("neutral-primary"), 0.8);
      }
    }
  }
}

.conditionTypeMarker {
  display: inline-flex;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
}

.emptyState {
  padding: 18px;
  border-radius: 12px;
  font-size: 14px;
  text-align: center;
  @include themify($themes) {
    color: themed("neutral-primary");
    border: 1px dashed themed("neutral-secondary");
    background-color: rgba(themed("main-secondary"), 0.55);
  }
}

.conditionCardList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.conditionCard {
  border-radius: 16px;
  @include themify($themes) {
    border: 1px solid themed("neutral-secondary");
    background-color: rgba(themed("main-secondary"), 0.75);
  }

  .conditionCardHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 18px;
    &:not(:last-child) {
      @include themify($themes) {
        border-bottom: 1px solid themed("neutral-secondary");
        background-color: rgba(themed("neutral-secondary"), 0.15);
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;
      }
    }

    .descriptionInput {
      background: transparent !important;
      border: none !important;
      outline: none !important;
      font-size: 14px;
      padding: 0 !important;
      height: fit-content;
      border-radius: 0;
      @include themify($themes) {
        color: themed("neutral-primary");
      }
    }
  }
}

.conditionCardHeading {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;

  .conditionTypeMarker {
    position: relative;
    top: 1px;
  }
}

.conditionCardTitle {
  min-width: 0;
  line-height: 1;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    @include themify($themes) {
      color: themed("neutral-primary");
    }
  }
}

.conditionActionsMenu {
  flex-shrink: 0;
}

.conditionCardBody {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
}

.conditionEditorOr {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  @include themify($themes) {
    color: themed("neutral-primary");
  }
}

.floatingLabelField {
  position: relative;

  label {
    font-size: 12px !important;
    font-weight: 400;
    position: absolute;
    top: 0;
    left: 12px;
    transform: translateY(-50%);
    z-index: 1;
    @include themify($themes) {
      background-color: themed("main-secondary");
    }
  }
}

.fieldsGroup {
  display: flex;
  gap: 16px;
  width: 100%;
}

.conditionForm {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  .textInput {
    height: 44px;
    min-height: 44px;
  }

  .fill {
    flex: 1;
  }
}

.compactField {
  width: 140px;
}

.checkboxField {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;

  @include themify($themes) {
    color: themed("main-primary");
  }

  label {
    user-select: none;
    &:hover {
      cursor: pointer;
    }
  }
}

.invalidExpression {
  font-size: 13px;
  @include themify($themes) {
    color: themed("error-primary");
  }
}

.conditionMax {
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.expression {
  position: relative;
  display: flex;
  align-items: center;

  .expressionInput {
    width: 100%;
    padding-left: 18px;
    padding-right: 34px;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .expressionPrefix,
  .expressionSuffix {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    font-size: 16px;
    left: 8px;
    @include themify($themes) {
      color: rgba(themed("neutral-primary"), 0.8);
    }
  }

  .expressionSuffix {
    left: unset;
    right: 10px;
  }
}

.blue {
  @include themify($themes) {
    background-color: rgba(themed("tuvis-primary"), 0.9);
  }
}

.red {
  @include themify($themes) {
    background-color: rgba(themed("error-primary"), 0.75);
  }
}

.purple {
  @include themify($themes) {
    background-color: rgba(themed("tvs-purple"), 0.9);
  }
}

.orange {
  @include themify($themes) {
    background-color: rgba(themed("tvs-orange"), 0.9);
  }
}

@media (max-width: 900px) {
  .dlpModal {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px);

    .main {
      padding: 4px 16px 8px;
      margin: 0 -16px;
    }
  }
}
